import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { de } from 'date-fns/locale';
import { DayAheadData, IntradayData } from '../types/price';
import './VolumeChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface VolumeChartProps {
  data?: DayAheadData | IntradayData;
  title: string;
}

const VolumeChart: React.FC<VolumeChartProps> = ({
  data,
  title,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString);
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: title,
        align: 'start',
        font: {
          size: 16,
          weight: 'bold',
        },
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (context) => {
            const date = new Date(context[0].parsed.x);
            return date.toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
          },
          label: (context) => {
            let label = '';
            if (context.parsed.y !== null) {
              label = context.parsed.y.toLocaleString('de-DE', { 
                minimumFractionDigits: 1, 
                maximumFractionDigits: 1 
              }) + ' MWh';
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        adapters: {
          date: {
            locale: de,
          },
        },
        time: {
          displayFormats: {
            hour: 'HH:mm',
            day: 'dd.MM',
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11,
          },
          callback: function(value) {
            return (value as number).toLocaleString('de-DE') + ' MWh';
          },
        },
      },
    },
  };

  const chartData = {
    datasets: [{
      label: 'Volumen',
      data: data?.volumes.map(v => ({
        x: formatDate(v.timestamp),
        y: v.volume,
      })) || [],
      backgroundColor: 'rgba(75, 192, 192, 0.6)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 1,
      borderRadius: 4,
    }],
  };

  const totalVolume = data?.totalVolume;

  return (
    <div className="volume-chart-container">
      <Bar options={options} data={chartData} />
      {totalVolume && (
        <div className="total-volume">
          Gesamt: {totalVolume.toLocaleString('de-DE')} MWh
        </div>
      )}
    </div>
  );
};

export default VolumeChart;